import React, { Component } from 'react';
import Link from 'gatsby-link';
import Helmet from 'react-helmet';
import { parse as parseQueryString } from 'qs';
import styles from './styles.module.css';

import DropDown from '../../components/drop-down';
import BackgroundImage from '../../components/background-image';
import SearchBox from '../../components/search-box';
import ContributeBanner from '../../components/contribute-banner';
import PillButton from '../../components/pill-button';
import SiteFooter from '../../components/site-footer';

import ResultList from './result-list';
import SearchControls from './search-controls';

import RoomImage from '../../images/room.jpg';
import SearchIcon from '../../icons/Search';

import objects from '../../../data/glassData.js';
import searchFilterAndSortGlass from '../../utils/search/glass';

import BackgroundContainer from '../../components/background-container';

const INITAL_AMOUNT_SHOWN = 10;
const LOAD_MORE_INCREMENT_AMOUNT = 10;
const ALLOWED_FILTERS = [
  'glassType',
  'designer',
  'colour',
  'productionLocation',
  'productionMethod',
  'tablewareType'
];

export default class Glass extends Component {
  constructor(props) {
    super(props);

    const query = parseQueryString(
      typeof location !== 'undefined' &&
        location.search &&
        location.search.slice(1)
    );

    const searchTerm = query.term || '';

    const filters = {};

    if (query.type && (query.type === 'WINDOW' || query.type === 'TABLEWARE')) {
      filters.glassType = [query.type];
    }

    this.state = {
      searchTerm,
      appliedSearchTerm: searchTerm,
      sortBy: 'name:asc',
      filters,
      amountShown: INITAL_AMOUNT_SHOWN,
      results: searchFilterAndSortGlass(
        objects,
        searchTerm,
        'name:asc',
        filters
      ),
      mobileFiltersOpen: false
    };
  }

  componentWillMount() {
    this.generateFilters();
  }

  generateFilters = () => {
    const filters = {};

    Object.values(objects).forEach(object =>
      Object.entries(object.data || {})
        .filter(entry => ALLOWED_FILTERS.includes(entry[0]))
        .forEach(entry => {
          const key = entry[0];
          const value = entry[1];
          if (!filters[key]) {
            filters[key] = [value];
          } else if (!filters[key].includes(value)) {
            filters[key].push(value);
          }
        })
    );

    this.setState({
      filters: Object.keys(filters).reduce(
        (result, filterName) =>
          Object.assign(result, {
            // Default to empty list if not already set (via query string)
            [filterName]: this.state.filters[filterName] || []
          }),
        {}
      )
    });

    this.filters = filters;
  };

  updateSearchTerm = event => {
    this.setState({ searchTerm: event.target.value });
  };

  updateFilters = (filterName, filterList) => {
    ga('send', {
      hitType: 'event',
      eventCategory: 'Search',
      eventAction: 'Filter',
      eventLabel: 'glassPage'
    });
    const newFilterValues = Object.assign({}, this.state.filters, {
      [filterName]: filterList
    });
    this.setState(state => ({ filters: newFilterValues }));
    this.performSearch();
  };

  applySearchTerm = () => {
    this.setState(state => ({ appliedSearchTerm: state.searchTerm }));
    this.performSearch();
  };

  updateSortBy = event => {
    ga('send', {
      hitType: 'event',
      eventCategory: 'Search',
      eventAction: 'SortOrder',
      eventLabel: 'glassPage'
    });
    const value = event.target.value;
    this.setState(state => ({ sortBy: value }));
    this.performSearch();
  };

  performSearch = () => {
    ga('send', {
      hitType: 'event',
      eventCategory: 'Search',
      eventAction: 'Submit',
      eventLabel: 'glassPage'
    });
    this.setState(state => ({
      results: searchFilterAndSortGlass(
        objects,
        state.appliedSearchTerm,
        state.sortBy,
        state.filters
      )
    }));
  };

  showMoreResults = () => {
    this.setState({
      amountShown: this.state.amountShown + LOAD_MORE_INCREMENT_AMOUNT
    });
  };

  toggleMobileFiltersOpen = () => {
    this.setState(state => ({
      mobileFiltersOpen: !state.mobileFiltersOpen
    }));
  };

  render() {
    const { amountShown, results } = this.state;
    const showingAll = amountShown >= results.length;
    const amountShownText = `Found ${results.length} results`;

    return (
      <div>
        <Helmet title="Search Whitefriars Glass" />
        <h1 className="a11y-hide">Search Items</h1>
        <BackgroundContainer src={RoomImage}>
          <div className={styles.header}>
            <div>
              <div className={styles.searchBoxContainer}>
                <SearchBox
                  value={this.state.searchTerm}
                  onChange={this.updateSearchTerm}
                  applySearchTerm={this.applySearchTerm}
                />
              </div>
              <div className={styles.sortByContainer}>
                <DropDown
                  id="sort-by"
                  label="Sort by:"
                  options={[
                    { text: 'Name (A - Z)', value: 'name:asc' },
                    { text: 'Name (Z - A)', value: 'name:desc' }
                    // { text: 'Newest First', value: 'date:asc' },
                    // { text: 'Oldest First', value: 'date:desc' },
                  ]}
                  value={this.state.sortBy}
                  onChange={this.updateSortBy}
                />
              </div>

              <PillButton
                className={styles.mobileFiltersButton}
                text="Set Filters"
                onClick={this.toggleMobileFiltersOpen}
              />
            </div>
          </div>
        </BackgroundContainer>
        <div className={styles.bottom}>
          <SearchControls
            filters={this.filters}
            filterValues={this.state.filters}
            updateFilters={this.updateFilters}
            mobileFiltersOpen={this.state.mobileFiltersOpen}
            toggleMobileFiltersOpen={this.toggleMobileFiltersOpen}
          />
          <div className={styles.results}>
            <ContributeBanner />
            <div className={styles.resultsHeader}>
              <h2>Results</h2>
              <span>{amountShownText}</span>
            </div>
            {results.length === 0 ? (
              <div className={styles.noResultsMessage}>
                No results found. Couldn't find what you were looking for? Maybe
                help out by adding a new item.
              </div>
            ) : (
              <div className={styles.resultsList}>
                <ResultList
                  showMore={this.showMoreResults}
                  objects={results.slice(0, amountShown)}
                />
                <div className={styles.listEnd}>
                  <div className={styles.progress}>{amountShownText}</div>
                  {!showingAll && (
                    <PillButton
                      text="Load more"
                      onClick={this.showMoreResults}
                    />
                  )}
                </div>
              </div>
            )}
            <SiteFooter wrapped />
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/page-components/glass/index.js